/**
 * Canonical fingerprint of an offer calculation for autosave dirty detection.
 *
 * Full JSON.stringify(formik.values) loops forever after PUT: the API always
 * returns fresh updated_at / summary / offer_totals / line totals, Formik
 * enableReinitialize applies them, Autosave sees a "change", PUTs again.
 *
 * Hash only the fields we actually persist (same whitelist spirit as
 * buildCalculationPayload / buildItemPayload), with stable number/null normals.
 */

import { readOfferGroupId } from "./offer-calc-item-groups";

const normalizeDec = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const normalized = /^-?\d+,\d+$/.test(s) ? s.replace(",", ".") : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : normalized;
};

const normalizeBool = (v) => Boolean(v);

/** Drop UI-only / server-echo keys that must not dirty autosave. */
const stripVolatileCustomFields = (item) => {
  const fields = item.custom_fields;
  if (!fields || typeof fields !== "object") return {};
  const next = { ...fields };
  // Mirrored as top-level products/notes in the wire payload.
  delete next.products_data;
  delete next.notes;
  delete next.client_row_key;
  // The API echoes these canonical item fields into JSONB. Count each once;
  // preserve custom-only legacy values until a top-level value is present.
  for (const key of [
    "price_mode", "hours", "hour_rate", "cost_hour_rate", "show_hours",
    "show_image", "show_url", "show_note_title", "note_title",
    "editable_quantity", "required_fields", "product_list",
  ]) {
    if (Object.prototype.hasOwnProperty.call(item, key)) delete next[key];
  }
  return next;
};

export function fingerprintCalculationItem(item) {
  if (!item || item.delete === true) return null;
  const mode =
    item.hours_pricing_mode || item.custom_fields?.hours_pricing_mode || null;
  const custom_fields = stripVolatileCustomFields(item);
  if (mode) custom_fields.hours_pricing_mode = mode;

  return {
    id: item.id ?? null,
    product_id: item.product_id ?? item.product?.id ?? null,
    description: item.description ?? null,
    quantity: normalizeDec(item.quantity),
    unit_id: item.unit_id ?? item.unit?.id ?? null,
    sales_price: normalizeDec(item.sales_price),
    purchase_price: normalizeDec(item.purchase_price),
    discount: normalizeDec(item.discount),
    surcharge: normalizeDec(item.surcharge),
    row_order: item.row_order ?? null,
    item_type: item.item_type ?? null,
    line_type: item.line_type ?? null,
    nested_calculation_id: item.nested_calculation_id ?? null,
    divide_by_quantity: normalizeBool(item.divide_by_quantity),
    price_mode: item.price_mode ?? null,
    hours: normalizeDec(item.hours) ?? 0,
    hour_rate: normalizeDec(item.hour_rate) ?? 0,
    cost_hour_rate: normalizeDec(item.cost_hour_rate) ?? 0,
    hours_pricing_mode: mode,
    products: item.products ?? [],
    product_list: item.product_list ?? item.custom_fields?.product_list ?? null,
    notes: item.notes ?? [],
    show_hours: normalizeBool(item.show_hours),
    show_image: item.show_image !== false,
    show_url: item.show_url !== false,
    show_note_title: item.show_note_title === true,
    note_title: item.note_title || "",
    required_fields: item.required_fields || {},
    editable_quantity: item.editable_quantity !== false,
    custom_fields,
    offer_group_id: readOfferGroupId(item),
  };
}

/**
 * Stable string for Autosave equality. Intentionally omits:
 * updated_at, created_at, summary, totals, offer_totals, offer_feature_flags,
 * nested product/unit objects, module_result (server-evaluated), draggableId.
 */
export function calculationPersistFingerprint(calc) {
  if (!calc) return "";
  const snapshot = {
    id: calc.id ?? null,
    original_calculation_id: calc.original_calculation_id ?? null,
    title: calc.title ?? null,
    description: calc.description ?? null,
    calculation_type:
      typeof calc.calculation_type === "string"
        ? calc.calculation_type
        : calc.calculation_type?.key ?? null,
    calculation_type_id:
      calc.calculation_type_id ||
      (typeof calc.calculation_type === "object"
        ? calc.calculation_type?.id
        : null) ||
      null,
    active: calc.active !== false,
    show_hours: normalizeBool(calc.show_hours),
    show_quantities: normalizeBool(calc.show_quantities),
    show_products: normalizeBool(calc.show_products),
    show_setup_only: normalizeBool(calc.show_setup_only),
    show_quantities_for_offer_item_only: normalizeBool(
      calc.show_quantities_for_offer_item_only
    ),
    include_product_short_description: normalizeBool(
      calc.include_product_short_description
    ),
    module_enabled: normalizeBool(calc.module_enabled),
    module_graph: calc.module_graph || {},
    items: (calc.items || [])
      .map(fingerprintCalculationItem)
      .filter(Boolean),
  };
  return JSON.stringify(snapshot);
}
